import { React, useState, useEffect, useContext, Fragment } from "react";
import styles from "./PCMain.module.css";
import axios from "axios";
import { Chart } from "react-google-charts";
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from "react-router-dom";
import Moment from 'moment';
import "moment/locale/ko";
import GridNoRow from '../../../common/GridNoRow';
import { AppContext } from '../../../AppStore';
import { CircularProgress, Fade, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';

// 최근 7일 [{label, value}] 집계
const last7Days = (rows) => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
        const dt = Moment().subtract(i, 'days');
        days.push({ key: dt.format('YYYY-MM-DD'), label: dt.format('MM/DD') + '(' + dt.format('dd') + ')', value: 0 });
    }
    const map = {};
    days.forEach(d => map[d.key] = d);
    rows.forEach(r => {
        const k = (r.d || '').substring(0, 10);
        if (map[k]) map[k].value += 1;
    });
    return days;
};

// 건수형 KPI 카드 : 큰 숫자(위) + 최근 7일 막대(아래)
const NewCountCard = (title, rows, field, color, type, setModalNew) => {
    const week = last7Days(rows);
    const mx = Math.max(1, ...week.map(d => d.value));
    return (
        <div key={title} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', height: 250, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontWeight: 700, fontSize: 16 }}>{title}</span>
                <IconButton size="small" onClick={() => setModalNew({ title, type, columns: [{ field, headerName: title, width: 550 }], rows })} sx={{ p: '5px' }}>
                    <TableChartOutlinedIcon sx={{ fontSize: 20, color: '#6b7280' }} />
                </IconButton>
            </div>
            {/* 큰 숫자 */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
                <span
                    onClick={() => setModalNew({ title, type, columns: [{ field, headerName: title, width: 550 }], rows })}
                    onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                    style={{ fontSize: 42, fontWeight: 800, color, lineHeight: 1, cursor: 'pointer', textUnderlineOffset: 5, textDecorationThickness: 2 }}>{rows.length}</span>
                <span style={{ fontSize: 13, color: '#9ca3af' }}>건 · 최근 7일</span>
            </div>
            {/* 최근 7일 막대 */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <div style={{ display: 'flex', gap: 8, height: 78, alignItems: 'flex-end' }}>
                    {week.map((d, i) => {
                        const h = d.value === 0 ? 0 : Math.max(14, Math.round(d.value / mx * 100));
                        return (
                            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                                <span style={{ fontSize: 11, color: '#6b7280', height: 14, fontWeight: 600 }}>{d.value > 0 ? d.value : ''}</span>
                                {/* 퍼센트(%) 높이를 계산할 수 있도록 높이가 고정(60px)된 부모 div 추가 */}
                                <div style={{ height: 60, width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                                    <div style={{ width: '70%', maxWidth: 22, height: d.value === 0 ? 2 : `${h}%`, minHeight: d.value === 0 ? 2 : 12, borderRadius: '4px 4px 0 0', background: d.value === 0 ? '#eef1f4' : color }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    {week.map((d, i) => <span key={i} style={{ flex: 1, textAlign: 'center', fontSize: 10, color: '#4b5563', fontWeight: 600, whiteSpace: 'nowrap' }}>{d.label}</span>)}
                </div>
            </div>
        </div>
    );
};

const Main = () => {
    const appStore = useContext(AppContext);
    const mainScreenType = String(localStorage.getItem("main_screen_type"));
    const [rowStock, setRowStock] = useState([]);
    const [columnStock, setColumnStock] = useState([
        { field: 'id', headerName: 'item_id', width: 0, headerAlign: 'center' },
        { field: 'item_data', headerName: '품목정보', width: 350, headerAlign: 'center', align:'left' },
        { field: 'dp_stock', headerName: '재고', width: 85, headerAlign: 'center', align:'right' },
        { field: 'dp_use_stock', headerName: '사용가능재고', width: 120, headerAlign: 'center', align:'right' }
    ]);
    const [rowOrder, setRowOrder] = useState([]);
    const [columnOrder, setColumnOrder] = useState([
        { field: 'id', headerName: 'order_id', width: 0, headerAlign: 'center' },
        { field: 'order_data', headerName: '수주정보', width: 550, headerAlign: 'center', align:'left' }
    ]);
    const [rowPurchase, setRowPurchase] = useState([]);
    const [columnPurchase, setColumnPurchase] = useState([
        { field: 'id', headerName: 'purchase_id', width: 0, headerAlign: 'center' },
        { field: 'purchase_data', headerName: '발주정보', width: 550, headerAlign: 'center', align:'left' }
    ]);
    const [rowIncoming, setRowIncoming] = useState([]);
    const [columnIncoming, setColumnIncoming] = useState([
        { field: 'id', headerName: 'incoming_id', width: 0, headerAlign: 'center' },
        { field: 'incoming_data', headerName: '입고정보', width: 550, headerAlign: 'center', align:'left' }
    ]);
    const [rowOutgoing, setRowOutgoing] = useState([]);
    const [columnOutgoing, setColumnOutgoing] = useState([
        { field: 'id', headerName: 'outgoing_id', width: 0, headerAlign: 'center' },
        { field: 'outgoing_data', headerName: '출하정보', width: 550, headerAlign: 'center', align:'left' }
    ]);
    const [rowQualityMeasurement, setRowQualityMeasurement] = useState([]);
    const [rowWork, setRowWork] = useState([]);
    const [columnQualityMeasurement, setColumnQualityMeasurement] = useState([
        { field: 'id', headerName: 'quality_measurement_id', width: 0, headerAlign: 'center' },
        { field: 'quality_measurement_data', headerName: '품질측정정보', width: 550, headerAlign: 'center', align:'left' }
    ]);

    const [machineReady, setMachineReady] = useState('N');
    const [machine, setMachine] = useState([]);
    const [modalNew, setModalNew] = useState(null);
    const navigate = useNavigate();

    // PC 전용 라우팅 맵
    const pcDetailRouteMap = {
        order: (id) => `/p/order/${id}`,
        outgoing: (id) => `/p/outgoing/${id}`,
        incoming: (id) => `/p/incoming/${id}`,
        purchase: (id) => `/p/purchase/${id}`,
        quality_measurement: (id) => `/p/quality_measurement/${id}`,
        stock: () => `/p/stock_info`,
        work: (id) => `/p/produce_work/${id}`
    };
    const handleRowClick = (type) => (params) => {
        const id = params?.row?.id ?? params?.id;
        const buildPath = pcDetailRouteMap[type];
        if (!buildPath) return;
        navigate(buildPath(id));
    };

    const [projectPie, setProjectPie] = useState(
        [[
        ["비율", "프로젝트"],
        [0, ""]
        ],
        [
        ["비율", "프로젝트"],
        [0, ""]
        ]]
    );
    const projectPie_option = {
        title: "프로젝트",
        titleTextStyle: { fontSize: 18, fontName: 'Arial',},
        colors: ["rgb(232, 176, 141)","rgb(206, 141, 232)","rgb(141, 231, 232)", "rgb(232, 141, 141)", "rgb(151, 236, 91)"],
        legend: {position: "right", alignment: "center",textStyle: {color: "#233238",fontSize: 13,},},
    };
    const [runPie, setRunPie] = useState(
        [[
            ["비율", "가동상태"],
            ["가동", 1],
            ["정지", 1],
            ["홀드", 1],
            ["MSRT", 1],
            ["오프라인", 1]
        ],[
            ["비율", "가동상태"],
            ["가동", 1],
            ["정지", 1],
            ["홀드", 1],
            ["MSRT", 1],
            ["오프라인", 1]
        ]]
    );
    const runPie_option = {
        title: "가동상태",
        titleTextStyle: { fontSize: 18, fontName: 'Arial',},
        colors: ["rgb(104, 171, 38)","#D84B16","#FFB737","#F1A7C9", "rgb(57, 54, 54)"],
        legend: {position: "right", alignment: "center",textStyle: {color: "#233238",fontSize: 13,},},
    };
    const [autPie, setAutPie] = useState(
        [[
            ["비율", "가동모드"],
            ["MDI", 1],
            ["MEM", 1],
            ["EDIT", 1],
            ["HND", 1],
            ["JOG", 1],
            ["REF", 1],
            ["ETC", 1],
        ],[
            ["비율", "가동모드"],
            ["MDI", 1],
            ["MEM", 1],
            ["EDIT", 1],
            ["HND", 1],
            ["JOG", 1],
            ["REF", 1],
            ["ETC", 1],
        ]]
    );
    const autPie_option = {
        title: "가동모드",
        titleTextStyle: { fontSize: 18, fontName: 'Arial',},
        colors: ["rgb(245, 123, 123)", "rgb(232, 215, 105)","rgb(128, 236, 104)","rgb(88, 136, 233)", "rgb(212, 95, 239)", "rgb(57, 54, 54)", "rgb(211, 141, 43)"],
        legend: {position: "right", alignment: "center",textStyle: {color: "#233238",fontSize: 13,},},
    };
    const [statusPie, setStatusPie] = useState([]);
    const statusPie_option = {
        title: "가동상태",
        titleTextStyle: { fontSize: 18, fontName: 'Arial',},
        colors: ["rgb(104, 171, 38)","#D84B16"],
        legend: {position: "right", alignment: "center",textStyle: {color: "#233238",fontSize: 13,},},
    };
    const [errorPie, setErrorPie] = useState([]);
    const errorPie_option = {
        title: "불량율",
        titleTextStyle: { fontSize: 18, fontName: 'Arial',},
        colors: ["#2196F3","#F44336"],
        legend: {position: "right", alignment: "center",textStyle: {color: "#233238",fontSize: 13,},},
    };
    const [errorTypePie, setErrorTypePie] = useState([]);
    const errorTypePie_option = {
        title: "불량유형",
        titleTextStyle: { fontSize: 18, fontName: 'Arial',},
        colors: ["#D84B16","#FFB737","rgb(232, 141, 141)"],
        legend: {position: "right", alignment: "center",textStyle: {color: "#233238",fontSize: 12.5,},},
    };
    const [timeLineData, setTimeLineData] = useState([
            [
            { type: "string", id: "Room" },
            { type: "string", id: "Name" },
            { type: "date", id: "Start" },
            { type: "date", id: "End" },
            ],
            [
                "기간",
                "오프라인",
                new Date(0, 0, 0, 0, 0, 0),
                new Date(0, 0, 0, 8, 0, 0),
            ],
            [
                "기간",
                "가동",
                new Date(0, 0, 0, 8, 0, 0),
                new Date(0, 0, 0, 16, 0, 0),
            ],
            [
                "기간",
                "정지",
                new Date(0, 0, 0, 16, 0, 0),
                new Date(0, 0, 0, 24, 0, 0),
            ],
    ]);
    const [timeLineOption, setTimeLineOption] = useState({
        timeline: { showRowLabels: false },
        avoidOverlappingGridLines: false,
        colors: ["#cbb69d", "#603913", "#c69c6e"],
    });
    const [modbusLineData, setModbusLineData] = useState({});

    const formatTime = (timeStr) => {
        if (!timeStr) return '';
        const parts = timeStr.split(':');
        if (parts.length !== 3) return timeStr;
        return `${parts[0]}H ${parts[1]}M ${parts[2]}S`;
    };

    const MainSearch = async () => {
        if (String(mainScreenType) !== '1') return;

        try {
            // 1) 첫 조회
            const resp = await axios.get('/api/p/MachineMonitoring/Select');
            const rows = resp?.data ?? [];
            
            // === 가공 결과 컨테이너 ===
            const machineInfo = [];
            const status_array = [];
            const error_array = [];
            const error_type_array = [];

            let hasType1 = false;
            // 2) 행 단위 가공
            for (let i = 0; i < rows.length; i++) {
                const data = rows[i];
                
                if (String(data.machine_type) === '1') hasType1 = true;

                // 공통 임시값
                let temp_percent = 0;
                let temp_percent_color = 'white';

                // run/aut 상태 계산
                const temp_status_run = (data.status_run !== undefined ? data.status_run : data.status);
                let temp_status_run_name = '';
                let temp_status_run_color = 'white';

                const temp_status_aut = data.status_aut;
                let temp_status_aut_name = '';

                // === 타입별 처리 ===
                if (data.machine_type == '1') {
                    // (1) 달성률 색상 계산
                    if (Number(data.sum_produce_count) > 0) {
                        const target = Number(data.target_count) || 0;
                        const produce = Number(data.produce_count) || 0;
                        if (target > 0) temp_percent = Math.floor((produce / target) * 100);

                        if (temp_percent >= 100) {
                            temp_percent_color = 'rgb(90, 150, 70)';   // 안정
                        }
                        if (temp_percent <= 50) {
                            temp_percent_color = 'rgb(200, 80, 80)';   // 위험
                        } else if (temp_percent > 50 && temp_percent <= 80) {
                            temp_percent_color = 'rgb(210, 140, 60)';  // 주의
                        } else if (temp_percent > 80 && temp_percent <= 99) {
                            temp_percent_color = 'rgb(160, 150, 80)';  // 거의 안정
                        }
                    }

                    // (2) 가동상태 이름/색상
                    if (temp_status_run == 0) {
                        temp_status_run_name = '오프라인'; temp_status_run_color = 'rgb(57, 54, 54)';
                    } else if (temp_status_run == 1) {
                        temp_status_run_name = '정지'; temp_status_run_color = '#D84B16';
                    } else if (temp_status_run == 2) {
                        temp_status_run_name = '홀드'; temp_status_run_color = '#FFB737';
                    } else if (temp_status_run == 3) {
                        temp_status_run_name = '가동'; temp_status_run_color = 'rgb(104, 171, 38)';
                    } else if (temp_status_run == 4) {
                        temp_status_run_name = 'MSTR'; temp_status_run_color = '#F1A7C9';
                    } else if (temp_status_run == -1) {
                        temp_status_run_name = '미 연결'; temp_status_run_color = 'rgba(100,116,139,.5)';
                    } else {
                        temp_status_run_name = ''; temp_status_run_color = '#bdbdbd';
                    }

                    // (3) 가동모드 이름
                    if (temp_status_aut == '0') temp_status_aut_name = 'MDI';
                    else if (['10','2','6','7','8'].includes(String(temp_status_aut))) temp_status_aut_name = 'ETC';
                    else if (temp_status_aut == '3') temp_status_aut_name = 'EDIT';
                    else if (temp_status_aut == '4') temp_status_aut_name = 'HND';
                    else if (temp_status_aut == '5') temp_status_aut_name = 'JOG';
                    else if (temp_status_aut == '9') temp_status_aut_name = 'REF';
                    else if (temp_status_aut == '1') temp_status_aut_name = 'MEM';
                    else if (temp_status_aut == -1) temp_status_aut_name = '미 연결';
                    else temp_status_aut_name = '';

                } else if (data.machine_type == '2' || data.machine_type == '4') {
                    // (1) 달성률 색상 계산 (sum_produce_count / target_count)
                    if (Number(data.sum_produce_count) > 0) {
                        const target = Number(data.target_count) || 0;
                        const sumProd = Number(data.sum_produce_count) || 0;
                        if (target > 0) temp_percent = Math.floor((sumProd / target) * 100);

                        if (temp_percent >= 100) {
                            temp_percent_color = 'rgb(90, 150, 70)';   // 안정
                        }
                        if (temp_percent <= 50) {
                            temp_percent_color = 'rgb(200, 80, 80)';   // 위험
                        } else if (temp_percent > 50 && temp_percent <= 80) {
                            temp_percent_color = 'rgb(210, 140, 60)';  // 주의
                        } else if (temp_percent > 80 && temp_percent <= 99) {
                            temp_percent_color = 'rgb(160, 150, 80)';  // 거의 안정
                        }
                    }

                    // (2) 가동상태 이름/색상 (2/4 전용)
                    if (temp_status_run == 0) {
                        temp_status_run_name = '정지'; temp_status_run_color = '#D84B16';
                    } else if (temp_status_run == 1) {
                        temp_status_run_name = '가동'; temp_status_run_color = 'rgb(104, 171, 38)';
                    } else if (temp_status_run == -1) {
                        temp_status_run_name = '미 연결'; temp_status_run_color = 'rgba(100,116,139,.5)';
                    } else {
                        temp_status_run_name = ''; temp_status_run_color = '#bdbdbd';
                    }
                } else if (data.machine_type == '5') {
                    if (temp_status_run == 1) {
                        temp_status_run_name = '가동'; temp_status_run_color = 'rgb(104, 171, 38)';
                    } else if (temp_status_run == 0) {
                        temp_status_run_name = '정지'; temp_status_run_color = '#D84B16';
                    } else if (temp_status_run == -1) {
                        temp_status_run_name = '미 연결'; temp_status_run_color = 'rgba(100,116,139,.5)';
                    } else {
                        temp_status_run_name = ''; temp_status_run_color = '#bdbdbd';
                    }
                }

                // (4) 화면에 쓸 기계 정보 push
                if (['1', '2', '4', '5'].includes(String(data.machine_type))) {
                    machineInfo.push({
                        machine_id: data.machine_id,
                        machine_name: data.machine_name,
                        machine_type: data.machine_type,
                        machine_max_datetime: data.max_datetime,
                        target_count: (typeof data.target_count === 'undefined' ? "0" : data.target_count),
                        produce_count: (typeof data.produce_count === 'undefined' ? "0" : data.produce_count),
                        project_number: (typeof data.project_number === 'undefined' ? "" : data.project_number),
                        project_name: (typeof data.project_name === 'undefined' ? "" : data.project_name),
                        progress_percent: (typeof data.produce_count === 'undefined' && typeof data.sum_produce_count === 'undefined') ? "0" : temp_percent,
                        progress_percent_color: temp_percent_color,
                        run_status: temp_status_run_name,
                        run_color: temp_status_run_color,
                        aut_status: temp_status_aut_name,
                        sum_produce_time: data.sum_produce_time,
                        sum_produce_count: data.sum_produce_count,
                        sum_ok_count: (typeof data.sum_ok_count === 'undefined' ? "0" : data.sum_ok_count),
                        sum_ng_count: (typeof data.sum_ng_count === 'undefined' ? "0" : data.sum_ng_count),
                        ng1_count: (typeof data.ng1_count === 'undefined' ? "0" : data.ng1_count),
                        ng2_count: (typeof data.ng2_count === 'undefined' ? "0" : data.ng2_count),
                        ng3_count: (typeof data.ng3_count === 'undefined' ? "0" : data.ng3_count),
                        ng1_label: (typeof data.ng1_label === 'undefined' ? "" : data.ng1_label),
                        ng2_label: (typeof data.ng2_label === 'undefined' ? "" : data.ng2_label),
                        ng3_label: (typeof data.ng3_label === 'undefined' ? "" : data.ng3_label),
                        raw_total_part: (typeof data.raw_total_part === 'undefined' ? "" : data.raw_total_part),
                        raw_cut_time: (typeof data.raw_cut_time === 'undefined' ? "" : data.raw_cut_time),
                        raw_cycle_time: (typeof data.raw_cycle_time === 'undefined' ? "" : data.raw_cycle_time),
                        raw_general_cut_time: (typeof data.raw_general_cut_time === 'undefined' ? "" : data.raw_general_cut_time),
                        raw_model: (typeof data.raw_model === 'undefined' ? "" : data.raw_model),
                        raw_part: (typeof data.raw_part === 'undefined' ? "" : data.raw_part),
                        raw_run_time: (typeof data.raw_run_time === 'undefined' ? "" : data.raw_run_time),
                        raw_target_part: (typeof data.raw_target_part === 'undefined' ? "" : data.raw_target_part),
                        raw_total_time: (typeof data.raw_total_time === 'undefined' ? "" : data.raw_total_time),
                        raw_type: (typeof data.raw_type === 'undefined' ? "" : data.raw_type),
                        val1: data.val1, val1_name: data.val1_name,
                        val2: data.val2, val2_name: data.val2_name,
                        val3: data.val3, val3_name: data.val3_name,
                        val4: data.val4, val4_name: data.val4_name,
                        val5: data.val5, val5_name: data.val5_name,
                        val6: data.val6, val6_name: data.val6_name,
                        val7: data.val7, val7_name: data.val7_name,
                        val8: data.val8, val8_name: data.val8_name,
                        val9: data.val9, val9_name: data.val9_name,
                    });

                    if(data.machine_type == '2'){
                        axios.get('/api/p/MachineRuningStatus/TimeLineData',
                            {
                                params : {
                                    id : data.machine_id
                                }
                            },
                            { withCredentials: true }
                        )
                        .then((response2) => {
                            let temp_array = [ 
                                [
                                    { type: "string", id: "Room" },
                                    { type: "string", id: "Name" },
                                    { type: "date", id: "Start" },
                                    { type: "date", id: "End" },
                                ],
                            ]
                            let temp_color_array = []
                            let last_hour = ''
                            let last_min = ''
                            let last_sec = ''
                            let temp_start_hour = '';
                            let temp_start_min = '';
                            let temp_start_sec = '';
                            let temp_end_hour = '';
                            let temp_end_min = '';
                            let temp_end_sec = '';
                            let temp_status = '';
                            let temp_status_name = '';
                            let temp_status_color = '';

                            let status_0 = 'N';
                            let status_1 = 'N';
                            let status_2 = 'N';
                            let status_3 = 'N';
                            let status_4 = 'N'; 
                            for(let i = 0; i < response2.data.length; i++) {
                                let addYN = 'N';
                                let data = response2.data[i];

                                if(data.status_run !== temp_status){
                                    temp_start_hour = Moment(data.start_datetime).format('HH');
                                    temp_start_min = Moment(data.start_datetime).format('mm');
                                    temp_start_sec = Moment(data.start_datetime).format('ss');
                                }
                                
                                temp_status = data.status_run;
                                
                                if(i === (response2.data).length-1 || temp_status !== (response2.data[i+1]).status_run){
                                    
                                    addYN = 'Y'
                                    temp_end_hour = Moment(data.end_datetime).format('HH');
                                    temp_end_min = Moment(data.end_datetime).format('mm');
                                    temp_end_sec = Moment(data.end_datetime).format('ss');

                                    if(String(data.machine_type) === '1'){
                                        if(Number(temp_status) === 0 || Number(temp_status) === -1){
                                            temp_status_name = '오프라인'
                                            temp_status_color = 'rgb(57, 54, 54)'
                                            if(status_0 === 'N'){
                                                temp_color_array = temp_color_array.concat('rgb(57, 54, 54)')
                                            }
                                            status_0 = 'Y'
                                        } else if (Number(temp_status) === 1){
                                            temp_status_name = '정지'
                                            temp_status_color = '#D84B16'
                                            if(status_1 === 'N'){
                                                temp_color_array = temp_color_array.concat('#D84B16')
                                            }
                                            status_1 = 'Y'
                                        } else if (Number(temp_status) === 2){
                                            temp_status_name = '홀드'
                                            temp_status_color = '#FFB737'
                                            if(status_2 === 'N'){
                                                temp_color_array = temp_color_array.concat('#FFB737')
                                            }
                                            status_2 = 'Y'
                                        } else if (Number(temp_status) === 3){
                                            temp_status_name = '가동'
                                            temp_status_color = 'rgb(104, 171, 38)'
                                            if(status_3 === 'N'){
                                                temp_color_array = temp_color_array.concat('rgb(104, 171, 38)')
                                            }
                                            status_3 = 'Y'
                                        } else if (Number(temp_status) === 4){
                                            temp_status_name = 'MSTR'
                                            temp_status_color = '#F1A7C9'
                                            if(status_4 === 'N'){
                                                temp_color_array = temp_color_array.concat('#F1A7C9')
                                            }
                                            status_4 = 'Y'
                                        }
                                    } else if(String(data.machine_type) === '2' || String(data.machine_type) === '4'){
                                        if (Number(temp_status) === 0 || Number(temp_status) === -1){
                                            temp_status_name = '정지'
                                            temp_status_color = '#D84B16'
                                            if(status_1 === 'N'){
                                                temp_color_array = temp_color_array.concat('#D84B16')
                                            }
                                            status_1 = 'Y'
                                        } else if (Number(temp_status) === 1){
                                            temp_status_name = '가동'
                                            temp_status_color = 'rgb(104, 171, 38)'
                                            if(status_3 === 'N'){
                                                temp_color_array = temp_color_array.concat('rgb(104, 171, 38)')
                                            }
                                            status_3 = 'Y'
                                        }
                                    }
                                }
                                

                                if(addYN === 'Y'){
                                    let temp_data = [[ 
                                        "기간",
                                        temp_status_name,
                                        new Date(0, 0, 0, temp_start_hour, temp_start_min, temp_start_sec),
                                        new Date(0, 0, 0, temp_end_hour, temp_end_min, temp_end_sec),
                                    ]]
                                    if(temp_start_hour||'$'||temp_start_min||'$'||temp_start_sec !== temp_end_hour||'$'||temp_end_min||'$'||temp_end_sec){
                                        temp_array = temp_array.concat(temp_data)
                                    }
                                }
                            }
                            
                            last_hour = temp_end_hour;
                            last_min = temp_end_min;
                            last_sec = temp_end_sec;
                            let temp_data = [[
                                "기간",
                                '데이터 수집중',
                                new Date(0, 0, 0, last_hour, last_min, last_sec),
                                new Date(0, 0, 0, 24, 0, 0),
                            ]]
                            temp_array = temp_array.concat(temp_data)
                            temp_color_array = temp_color_array.concat('rgb(255, 255, 255)')
                            
                            setTimeLineOption(
                                {
                                    timeline: { showRowLabels: false },
                                    avoidOverlappingGridLines: false,
                                    colors: temp_color_array,
                                }
                            )
                            setTimeLineData(temp_array)
                        })
                        .catch((error) => {
                            appStore.capture();
                        })
                    }else if(data.machine_type == '5'){
                        axios.get('/api/p/MachineRuningStatus/ModbusTimeData',
                            {
                                params : {
                                    id : data.machine_id
                                }
                            },
                            { withCredentials: true }
                        )
                        .then((response2) => {
                            const list = response2.data || [];
                            if (list.length > 0) {
                                let headers = ["시간"];
                                let validKeys = [];
                                
                                const firstRow = list[0];
                                for(let k=1; k<=9; k++) {
                                    if (firstRow[`val${k}_name`] && String(firstRow[`val${k}_name`]).trim() !== "") {
                                        headers.push(firstRow[`val${k}_name`]);
                                        validKeys.push(`val${k}`);
                                    }
                                }
                                
                                if (validKeys.length > 0) {
                                    let chartData = [headers];
                                    list.forEach(row => {
                                        let rowData = [row.time_str];
                                        validKeys.forEach(k => {
                                            rowData.push(row[k] !== null ? Number(row[k]) : null);
                                        });
                                        chartData.push(rowData);
                                    });

                                    setModbusLineData(prev => ({
                                        ...prev,
                                        [data.machine_id]: chartData
                                    }));
                                }
                            }
                        })
                        .catch((error) => {
                            try { appStore?.capture?.(error); } catch {}
                        })
                    }
                    
                    if (data.machine_type == '2' || data.machine_type == '4') {
                        status_array.push([
                            ["상태", "가동율"],
                            ["가동", data.run_min ?? 0],
                            ["정지", data.stop_min ?? 0],
                        ]);
                        error_array.push([
                            ["상태", "불량율"],
                            ["OK", data.sum_ok_count ?? 0],
                            ["NG", data.sum_ng_count ?? 0],
                        ]);
                    } else {
                        status_array.push(null);
                        error_array.push(null);
                    }

                    if (data.machine_type == '4') {
                        error_type_array.push([
                            ["상태", "불량유형"],
                            [data.ng1_label, data.ng1_count ?? 0],
                            [data.ng2_label, data.ng2_count ?? 0],
                            [data.ng3_label, data.ng3_count ?? 0],
                        ]);
                    } else {
                        error_type_array.push(null);
                    }
                }
            }

            setStatusPie(status_array);
            setErrorPie(error_array);
            setErrorTypePie(error_type_array);
            setMachine(machineInfo);

            if (!hasType1) {
                setProjectPie([]);
                setAutPie([]);
                setRunPie([]);
                setMachineReady('Y');
                return;
            }

            const [projRes, autRes, runRes] = await Promise.all([
                axios.get('/api/p/MachineMonitoring/SelectProjectName'),
                axios.get('/api/p/MachineMonitoring/SelectAutStatus'),
                axios.get('/api/p/MachineMonitoring/SelectRunStatus'),
            ]);

            {
                const list = projRes?.data ?? [];
                let full_array = [];
                let temp_array = [];
                let data_machine_id = '0';

                for (let i = 0; i < list.length; i++) {
                    const d = list[i];
                    if (d.machine_id !== data_machine_id) {
                        full_array.push(temp_array);
                        data_machine_id = d.machine_id;
                        temp_array = [["비율", "프로젝트"]];
                    }
                    temp_array = temp_array.concat([[d.project_name, d.sum_produce_count]]);
                }
                full_array.push(temp_array);
                full_array.splice(0, 1);
                setProjectPie(full_array);
            }

            {
                const list = autRes?.data ?? [];
                let MDI = 0, EDIT = 0, HND = 0, JOG = 0, REF = 0, MEM = 0, ETC = 0;
                let full_array = [];
                let data_machine_id = '0';

                for (let i = 0; i < list.length; i++) {
                    const d = list[i];
                    if (d.machine_id !== data_machine_id) {
                        if (data_machine_id !== '0') {
                            full_array.push([
                            ["비율", "가동모드"],
                            ["MDI", MDI], ["MEM", MEM], ["EDIT", EDIT],
                            ["HND", HND], ["JOG", JOG], ["REF", REF], ["ETC", ETC],
                            ]);
                        }
                        data_machine_id = d.machine_id;
                        MDI = EDIT = HND = JOG = REF = MEM = ETC = 0;
                    }

                    if (d.status_aut === '0') MDI = d.sum_produce_time;
                    else if (['10','2','6','7','8'].includes(d.status_aut)) ETC += d.sum_produce_time;
                    else if (d.status_aut === '3') EDIT = d.sum_produce_time;
                    else if (d.status_aut === '4') HND = d.sum_produce_time;
                    else if (d.status_aut === '5') JOG = d.sum_produce_time;
                    else if (d.status_aut === '9') REF = d.sum_produce_time;
                    else if (d.status_aut === '1') MEM = d.sum_produce_time;
                }

                full_array.push([
                    ["비율", "가동모드"],
                    ["MDI", MDI], ["MEM", MEM], ["EDIT", EDIT],
                    ["HND", HND], ["JOG", JOG], ["REF", REF], ["ETC", ETC],
                ]);
                setAutPie(full_array);
                setMachineReady('Y');
            }

            {
                const list = runRes?.data ?? [];
                let reset = 0, stop = 0, hold = 0, start = 0, mstr = 0;
                let full_array = [];
                let data_machine_id = '0';

                for (let i = 0; i < list.length; i++) {
                    const d = list[i];
                    if (d.machine_id !== data_machine_id) {
                        if (data_machine_id !== '0') {
                            full_array.push([
                            ["상태", "가동상태"],
                            ["가동", start], ["정지", stop], ["홀드", hold],
                            ["MSTR", mstr],
                            ["오프라인", reset],
                            ]);
                        }
                        data_machine_id = d.machine_id;
                        reset = stop = hold = start = mstr = 0;
                    }

                    if (d.status_run === '0') reset = d.sum_produce_time;
                    else if (d.status_run === '1') stop = d.sum_produce_time;
                    else if (d.status_run === '2') hold = d.sum_produce_time;
                    else if (d.status_run === '3') start = d.sum_produce_time;
                    else if (d.status_run === '4') mstr = d.sum_produce_time;
                }

                full_array.push([
                    ["상태", "가동상태"],
                    ["가동", start], ["정지", stop], ["홀드", hold],
                    ["MSTR", mstr],
                    ["오프라인", reset],
                ]);
                setRunPie(full_array);
            }
        } catch (e) {
            try { appStore?.capture?.(e); } catch {}
            console.error(e);
        }
    };
    
    const CommonSearch = () => {
        axios.get('/api/Common/Main')
        .then((response) => {
            let stockArray = response.data.map((item, index) => ({
                id: item.item_id,
                item_data: item.item_name + ' ' +appStore.ITEM_FULL_SPEC_MAKE( item.item_spec, item.item_width, item.item_depth, item.item_height),
                dp_stock: Number(String(item.dp_stock || 0).replace(/,/g, '')).toLocaleString(),
                dp_use_stock: Number(String(item.dp_use_stock || 0).replace(/,/g, '')).toLocaleString(),
                safe_stock: item.dp_item_total_safe_stock_count || Number(item.item_total_safe_stock_count || 0).toLocaleString()
            }));
            stockArray = stockArray.filter((item, index) => {
                return typeof item.id !== 'undefined';
            });

            stockArray = stockArray.sort((a, b) => {
                return a.item_data < b.item_data ? -1 : a.item_data > b.item_data ? 1 : 0;
            });

            let orderArray = response.data.map((item, index) => ({
                id: item.order_id,
                order_data: item.order_data,
                d: item.order_date
            }));
            orderArray = orderArray.filter((item, index) => {
                return typeof item.id !== 'undefined' && item.id !== 0;
            });

            let purchaseArray = response.data.map((item, index) => ({
                id: item.purchase_id,
                purchase_data: item.purchase_data,
                d: item.purchase_date
            }));
            purchaseArray = purchaseArray.filter((item, index) => {
                return typeof item.id !== 'undefined' && item.id !== 0;
            });

            let incomingArray = response.data.map((item, index) => ({
                id: item.incoming_id,
                incoming_data: item.incoming_data,
                d: item.incoming_date
            }));
            incomingArray = incomingArray.filter((item, index) => {
                return typeof item.id !== 'undefined' && item.id !== 0;
            });

            let outgoingArray = response.data.map((item, index) => ({
                id: item.outgoing_id,
                outgoing_data: item.outgoing_data,
                d: item.outgoing_date
            }));
            outgoingArray = outgoingArray.filter((item, index) => {
                return typeof item.id !== 'undefined' && item.id !== 0;
            });

            let qualityMeasurementArray = response.data.map((item, index) => ({
                id: item.quality_measurement_id,
                d: (item.quality_measurement_datetime || '').substring(0,10),
                quality_measurement_data: item.quality_measurement_data + ' / ' + (typeof item.quality_measurement_value === 'undefined'?item.quality_measurement_option_name:item.quality_measurement_value)  + ' (' + item.quality_measurement_result + ') / ' + item.quality_measurement_user_name
            }));
            qualityMeasurementArray = qualityMeasurementArray.filter((item, index) => {
                return typeof item.id !== 'undefined' && item.id !== 0;
            });

            let workArray = response.data.map((item, index) => ({
                id: item.work_id,
                work_data: item.work_data,
                d: item.work_date
            }));
            workArray = workArray.filter((item, index) => {
                return typeof item.id !== 'undefined' && item.id !== 0;
            });
            
            setRowOrder(orderArray);
            setRowPurchase(purchaseArray);
            setRowIncoming(incomingArray);
            setRowOutgoing(outgoingArray);
            setRowQualityMeasurement(qualityMeasurementArray);
            setRowWork(workArray);
            setRowStock(stockArray);
        })
        .catch(function (error) {
        });
    };

    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .google-visualization-tooltip {
            max-width: none !important;   /* 기본 400px 제한 해제 */
            width: 250px !important;       /* 원하는 너비로 */
            white-space: normal;           /* 줄바꿈 허용 (가로 고정 늘리고 싶으면 'nowrap') */
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    useEffect(() => {
        appStore.setLoading(false);

        const fetchData = async () => {
            appStore.setLoading(true);
            try {
                await MainSearch(); 
                CommonSearch();
            } finally {
                appStore.setLoading(false); 
            }
        };

        fetchData();

        const interval = setInterval(() => {
            MainSearch();
        }, 30000);

        return () => clearInterval(interval);
    }, []);
    
    return (
        <div style={{ width: '100%', minHeight: 'auto', background: 'var(--bg)', padding: '20px 16px 48px', margin: 0, position: 'relative', boxSizing: 'border-box' }}>
            {/* ===== 상단 요약 (6개 건수 1줄 배치) ===== */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 24 }}>
                {[
                    { label: '생산\n작업', title: '생산작업', value: rowWork.length, color: '#7c5cff', rows: rowWork, field: 'work_data' },
                    { label: '수주\n정보', title: '수주정보', value: rowOrder.length, color: '#2f7ed8', rows: rowOrder, field: 'order_data' },
                    { label: '발주\n정보', title: '발주정보', value: rowPurchase.length, color: '#00bcd4', rows: rowPurchase, field: 'purchase_data' },
                    { label: '입고\n정보', title: '입고정보', value: rowIncoming.length, color: '#199e70', rows: rowIncoming, field: 'incoming_data' },
                    { label: '출하\n정보', title: '출하정보', value: rowOutgoing.length, color: '#f59e0b', rows: rowOutgoing, field: 'outgoing_data' },
                    { label: '품질\n측정', title: '품질측정정보', value: rowQualityMeasurement.length, color: '#ec4899', rows: rowQualityMeasurement, field: 'quality_measurement_data' },
                ].map((k, i) => {
                    const clickable = !!k.rows;
                    return (
                        <div key={i}
                            onClick={clickable ? () => setModalNew({ title: k.title, type: k.field.split('_')[0], columns: [{ field: k.field, headerName: k.title, width: 550 }], rows: k.rows }) : undefined}
                            onMouseEnter={(e) => { if (clickable) { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.10)'; e.currentTarget.querySelector('.sum-num').style.textDecoration = 'underline'; } }}
                            onMouseLeave={(e) => { if (clickable) { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.querySelector('.sum-num').style.textDecoration = 'none'; } }}
                            style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '18px 22px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', cursor: clickable ? 'pointer' : 'default', transition: 'box-shadow 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <span className="sum-label" style={{ fontSize: 17, color: '#374151', fontWeight: 700, lineHeight: 1.25, whiteSpace: 'pre-line' }}>{k.label}</span>
                            <span style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                                <span className="sum-num" style={{ fontSize: 48, fontWeight: 800, color: k.color, lineHeight: 1, textUnderlineOffset: 5, textDecorationThickness: 2 }}>{k.value}</span>
                                <span style={{ fontSize: 15, color: '#9ca3af', fontWeight: 600 }}>건</span>
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* ===== 설비 현황 ===== */}
            {mainScreenType === '1' && machine.length > 0 ?
            <>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', margin: '10px 4px 14px', display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={{ width: 4, height: 20, background: '#2f7ed8', borderRadius: 2 }} />설비
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 30 }}>
                {machine.map((item, index) => {
                    const t = String(item.machine_type);
                    const on = item.run_status === '가동' || item.run_status === 'MSTR';
                    const total = Number(item.sum_produce_count) || 0;
                    const ok = Number(item.sum_ok_count) || 0;
                    const ng = Number(item.sum_ng_count) || 0;
                    const runMin = Number(item.run_min) || 0, stopMin = Number(item.stop_min) || 0;
                    const runRate = (runMin + stopMin) > 0 ? Math.round(runMin / (runMin + stopMin) * 100) : 0;
                    const ngRate = total > 0 ? (ng / total * 100).toFixed(2) : '0.00';
                    const PIE_COLORS = ['#68ab26', '#e2703a', '#f0b429', '#2f7ed8', '#7c5cff', '#39362e', '#199e70'];
                    const pieOpt = { legend: { position: 'right', textStyle: { fontSize: 13 } }, chartArea: { width: '92%', height: '82%' }, pieHole: 0.45, colors: PIE_COLORS, pieSliceText: 'percentage', fontSize: 12 };
                    const gauge = (pct, color, label) => (
                        <div style={{ width: 118, height: 118, borderRadius: '50%', flexShrink: 0, background: `conic-gradient(${color} 0 ${pct}%, #eef1f4 ${pct}% 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ width: 88, height: 88, borderRadius: '50%', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ fontSize: 26, fontWeight: 800, color: '#1f2937' }}>{pct}%</span>
                                <span style={{ fontSize: 13, color: '#9ca3af' }}>{label}</span>
                            </div>
                        </div>
                    );
                    const statusMark = (info, color) => (
                        <div style={{ width: 118, height: 118, borderRadius: '50%', flexShrink: 0, border: `8px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: 18, fontWeight: 800, color: '#1f2937', textAlign: 'center', lineHeight: 1.2 }}>{info}</span>
                        </div>
                    );
                    const field = (label, value, color) => (
                        <div><div style={{ color: '#6b7280', fontSize: 13, marginBottom: 4 }}>{label}</div><b style={{ fontSize: 21, color: color || '#1f2937' }}>{value}</b></div>
                    );
                    const pieBox = (title, data) => (
                        <div style={{ flex: 1, minWidth: 220 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', textAlign: 'center', marginBottom: 4 }}>{title}</div>
                            <div style={{ height: 170 }}>
                                {machineReady === 'Y' && data ? <Chart chartType="PieChart" data={data} options={pieOpt} width="100%" height="100%" /> : null}
                            </div>
                        </div>
                    );
                    return (
                        <div key={index} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: '24px 28px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ fontWeight: 700, fontSize: 21, color: '#1f2937' }}>{item.machine_name}</span>
                                    {item.machine_max_datetime ? <span style={{ fontSize: 13, color: '#c0c4cc' }}>[{item.machine_max_datetime}]</span> : null}
                                </span>
                                <span style={{ fontSize: 14, fontWeight: 700, color: '#fff', background: item.run_color, padding: '6px 16px', borderRadius: 8 }}>{item.run_status || '-'}</span>
                            </div>

                            <div style={{ display: 'flex', gap: 34, alignItems: 'center', flexWrap: 'wrap' }}>
                                {t === '1' && <>
                                    {gauge(Number(item.progress_percent) || 0, Number(item.progress_percent) >= 100 ? '#199e70' : (on ? '#199e70' : '#9aa3af'), '달성률')}
                                    <div style={{ flex: '0 0 360px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px 24px' }}>
                                        {field('가동모드', item.aut_status || '-')}
                                        {field('프로젝트', item.project_name || '-')}
                                        {field('소요부품', item.raw_target_part || '0')}
                                        {field('가공부품', item.raw_part || '0')}
                                        {field('전체부품', item.raw_total_part || '0')}
                                        {field('운전시간', item.raw_run_time || '-')}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 500, display: 'flex', gap: 10 }}>
                                        {pieBox('가동상태', runPie[index])}
                                        {pieBox('가공모드', autPie[index])}
                                        {pieBox('프로젝트', projectPie[index])}
                                    </div>
                                </>}

                                {t === '4' && <>
                                    {gauge(Number(item.progress_percent) || 0, Number(item.progress_percent) >= 100 ? '#199e70' : (on ? '#199e70' : '#d98a00'), '달성률')}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,auto)', gap: '18px 40px' }}>
                                        {field('검사수', total.toLocaleString())}
                                        {field('양품', ok.toLocaleString(), '#199e70')}
                                        {field('불량', ng.toLocaleString(), '#e24b4a')}
                                        {field('불량률', ngRate + '%')}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 280 }}>
                                        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10 }}>불량 유형</div>
                                        {(() => {
                                            const arr = [
                                                { label: item.ng1_label || 'NG1', v: Number(item.ng1_count) || 0, c: '#2f7ed8' },
                                                { label: item.ng2_label || 'NG2', v: Number(item.ng2_count) || 0, c: '#d98a00' },
                                                { label: item.ng3_label || 'NG3', v: Number(item.ng3_count) || 0, c: '#199e70' },
                                            ];
                                            const mx = Math.max(1, ...arr.map(a => a.v));
                                            return arr.map((a, i) => (
                                                <div key={i} style={{ marginBottom: 9 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}><span>{a.label}</span><b>{a.v.toLocaleString()}</b></div>
                                                    <div style={{ height: 9, background: '#eef1f4', borderRadius: 5, overflow: 'hidden' }}><div style={{ width: a.v === 0 ? '0%' : `${Math.max(3, a.v / mx * 100)}%`, height: '100%', background: a.v === 0 ? 'transparent' : a.c, borderRadius: 5 }} /></div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </>}

                                {t === '5' && <>
                                    {statusMark(item.run_status || '-', on ? '#199e70' : (item.run_status === '미연결' ? '#cbd5e1' : '#d98a00'))}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,auto)', gap: '18px 36px' }}>
                                        {[1,2,3,4,5,6,7,8,9].map(n => (item[`val${n}_name`] ? <div key={n}>{field(item[`val${n}_name`], item[`val${n}`] ?? '-')}</div> : null))}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 320 }}>
                                        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>최근 30분 데이터</div>
                                        <div style={{ height: 160 }}>
                                            {machineReady === 'Y' && modbusLineData[item.machine_id] && modbusLineData[item.machine_id].length > 1 ?
                                                <Chart chartType="LineChart" data={modbusLineData[item.machine_id]} options={{ curveType: 'function', legend: { position: 'bottom' }, chartArea: { width: '88%', height: '66%' }, vAxis: { minValue: 0 }, pointSize: 4, colors: ['#2f7ed8', '#199e70', '#d98a00'] }} width="100%" height="100%" />
                                                : <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#b0b4bb', fontSize: 14 }}>수집된 데이터가 없습니다.</div>}
                                        </div>
                                    </div>
                                </>}

                                {t === '3' && <>
                                    {statusMark(item.run_status || '-', on ? '#199e70' : '#9aa3af')}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,auto)', gap: '18px 40px' }}>
                                        {field('전력(W)', item.power ?? '-')}
                                        {field('전압(V)', item.voltage ?? '-')}
                                        {field('전류(A)', item.current ?? '-')}
                                        {field('온도(℃)', item.temperature ?? '-')}
                                    </div>
                                </>}

                                {t === '2' && <>
                                    {statusMark(item.run_status || '-', on ? '#199e70' : (item.run_status === '미연결' ? '#cbd5e1' : '#d98a00'))}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,auto)', gap: '18px 36px' }}>
                                        {[1,2,3,4,5,6,7,8,9].map(n => (item[`val${n}_name`] ? <div key={n}>{field(item[`val${n}_name`], item[`val${n}`] ?? '-')}</div> : null))}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 340 }}>
                                        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>가동시간 (24시간)</div>
                                        {machineReady === 'Y' ? <Chart chartType="Timeline" data={timeLineData} options={timeLineOption} width="100%" height="140px" /> : null}
                                    </div>
                                </>}
                            </div>
                        </div>
                    );
                })}
            </div>
            </>
            : null }

            {/* ===== 품목재고 (단독 가로 스크롤 그래프) ===== */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', margin: '10px 4px 14px' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', display: 'flex', alignItems: 'center', gap: 9 }}>
                    <span style={{ width: 4, height: 20, background: '#199e70', borderRadius: 2 }} />품목재고
                </div>
                <IconButton size="small" onClick={() => setModalNew({ 
                    title: '품목재고', 
                    type: 'stock', 
                    columns: [
                        { field: 'item_data', headerName: '품목정보', flex: 1, minWidth: 200 }, 
                        { field: 'safe_stock', headerName: '안전재고', width: 110, align: 'right', headerAlign: 'center' },
                        { field: 'dp_stock', headerName: '재고', width: 110, align: 'right', headerAlign: 'center' }, 
                        { field: 'dp_use_stock', headerName: '사용가능재고', width: 110, align: 'right', headerAlign: 'center' }
                    ], 
                    rows: rowStock 
                })} sx={{ p: '5px' }}>
                    <TableChartOutlinedIcon sx={{ fontSize: 20, color: '#6b7280' }} />
                </IconButton>
            </div>
            
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '24px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: 24, display: 'flex', gap: 24, overflowX: 'auto', alignItems: 'flex-end', minHeight: 180 }}>
                {rowStock.length === 0 && <div style={{ color: '#9ca3af', fontSize: 15, textAlign: 'center', width: '100%', padding: '50px 0' }}>조회된 품목재고가 없습니다.</div>}
                {(() => {
                    const mx = Math.max(1, ...rowStock.map(s => Number(String(s.dp_stock).replace(/,/g, '')) || 0));
                    return rowStock.map((s) => {
                        const v = Number(String(s.dp_stock).replace(/,/g, '')) || 0;
                        const w = v === 0 ? 0 : Math.max(2, Math.round(v / mx * 100));
                        const col = v === 0 ? '#e24b4a' : (v <= mx * 0.2 ? '#d98a00' : '#199e70');
                        return (
                            <div key={s.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', width: 160, flexShrink: 0, gap: 8 }}>
                                <span style={{ fontSize: 14, fontWeight: 700, color: col }}>{v.toLocaleString()}</span>
                                <div style={{ width: 30, height: 100, background: '#eef1f4', borderRadius: '4px 4px 0 0', display: 'flex', alignItems: 'flex-end' }}>
                                    <div style={{ width: '100%', height: `${w}%`, background: v === 0 ? 'transparent' : col, borderRadius: '4px 4px 0 0', transition: 'height 0.3s ease' }} />
                                </div>
                                <span style={{ fontSize: 12, color: '#4b5563', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', textAlign: 'center', marginTop: 4 }} title={s.item_data}>{s.item_data}</span>
                            </div>
                        );
                    });
                })()}
            </div>

            {/* ===== 재고·물류·품질 (새 디자인 3x2) ===== */}
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', margin: '10px 4px 14px', display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={{ width: 4, height: 20, background: '#2f7ed8', borderRadius: 2 }} />업무 현황
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                {NewCountCard('생산작업', rowWork, 'work_data', '#7c5cff', 'work', setModalNew)}
                {NewCountCard('수주정보', rowOrder, 'order_data', '#2f7ed8', 'order', setModalNew)}
                {NewCountCard('발주정보', rowPurchase, 'purchase_data', '#00bcd4', 'purchase', setModalNew)}
                {NewCountCard('입고정보', rowIncoming, 'incoming_data', '#199e70', 'incoming', setModalNew)}
                {NewCountCard('출하정보', rowOutgoing, 'outgoing_data', '#f59e0b', 'outgoing', setModalNew)}
                {NewCountCard('품질측정정보', rowQualityMeasurement, 'quality_measurement_data', '#ec4899', 'quality_measurement', setModalNew)}
            </div>

            {/* 새 모달 */}
            <Dialog open={!!modalNew} onClose={() => setModalNew(null)} PaperProps={{ sx: { maxWidth: '700px' } }} fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 17, fontWeight: 700 }}>
                    {modalNew?.title}
                    <IconButton size="small" onClick={() => setModalNew(null)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                    <div style={{ height: 300, width: '100%' }}>
                        {modalNew && <DataGrid rows={modalNew.rows} columns={modalNew.columns} onRowClick={handleRowClick(modalNew.type)} density="compact" disableRowSelectionOnClick pageSizeOptions={[10, 25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} columnVisibilityModel={{ id: false }} localeText={{ noRowsLabel: '조회된 데이터가 없습니다.', MuiTablePagination: { labelRowsPerPage: '페이지당 행 수:' } }} sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }} />}
                    </div>
                </DialogContent>
            </Dialog>

        </div>
    )
}

export default Main;